import React from 'react';
import { isTouchDevice } from '@investoro/core';
import dayjs from 'dayjs';
import { calculateMinElementWidth } from 'utils/calculateElementMinWidth';
import { getNameFromModule } from 'utils/getNameFromModule';
import { getCompareEnum, ValueCompareEnum } from 'utils/helpers/getCompareEnum';
import { normalizeMetaKeyWords } from 'utils/normalizeMetaKeyWords';
import { SaleOfferResponseContent } from 'utils/state-managment/sale/saleOffer';
import { MetaValueDto, Module, SaleStatusDto } from 'utils/types/SaleModels';

import { InfoElementType } from 'components/common/Card/common/V2CardBodyDefault/InfoElements';
import { IconEnum } from 'components/common/Icon';
import { V2DetailsTabsProps } from 'components/common/Tabs/tabs/V2DetailsTab';

export type Offer = Omit<SaleOfferResponseContent, 'detailedContent'> & {
    detailedContent?: {
        description: string;
        metaValue?: MetaValueDto[];
    }
};

interface FieldConfig {
    icon: IconEnum;
    label: string;
    value: (offer: Offer) => string | undefined;
    predictedValue?: (offer: Offer) => string | undefined;
    prefix?: string;
    suffix?: string;
    url?: (offer: Offer) => string | undefined;
}

const tooltipText = 'Dane wygenerowane na podstawie predykcji AI';

export enum Variant {
    DIFFERENT = 'different',
    PREDICTED = 'predicted',
    DEFAULT = 'default'
}

const getVariantFromCompareEnum = (compareEnum: ValueCompareEnum): Variant => {
    switch (compareEnum) {
    case ValueCompareEnum.DIFFERENT_VALUES:
        return Variant.DIFFERENT;
    case ValueCompareEnum.PREDICTED_VALUE_ONLY:
        return Variant.PREDICTED;
    default:
        return Variant.DEFAULT;
    }
};

type InfoElementsStateType = Record<string, { hover: boolean; clicked: boolean }>;

const generateInfoElements = (
    offer: Offer,
    infoElementsState: InfoElementsStateType,
    hasPredictionRole: boolean,
    setInfoElementHover?: (offerId: number, elementKey: string, hover: boolean) => void,
    setInfoElementClicked?: (offerId: number, elementKey: string, clicked: boolean) => void
): InfoElementType[] => {
    const fields: {
        icon: IconEnum;
        valueKey: keyof Offer;
        predictedKey?: keyof Offer;
        prefix?: string;
        suffix?: string;
        tooltipText?: string;
    }[] = [
        { icon: IconEnum.BUILDING, valueKey: 'module' },
        { icon: IconEnum.WORK, valueKey: 'typeOfMarket', predictedKey: 'predictedTypeOfMarket', prefix: 'Rynek' },
        { icon: IconEnum.SQUARE, valueKey: 'area', predictedKey: 'predictedArea' },
        { icon: IconEnum.BED, valueKey: 'numberOfRooms', predictedKey: 'predictedNumberOfRooms', prefix: 'pokoje' },
        { icon: IconEnum.FLOOR_LEVEL, valueKey: 'floor', predictedKey: 'predictedFloor', suffix: 'piętro' },
        { icon: IconEnum.TWO_TYPE, valueKey: 'buildingType', predictedKey: 'predictedBuildingType' },
        { icon: IconEnum.CALENDAR, valueKey: 'builtYear', predictedKey: 'predictedBuiltYear' },
        { icon: IconEnum.PAINT_ROLLER, valueKey: 'buildingCondition', predictedKey: 'predictedBuildingCondition', tooltipText: 'Stan techniczny mieszkania' }
    ];

    return fields.map((field) => {
        const value = offer[field.valueKey] as string | number | undefined;
        const predictedValue = field.predictedKey ? (offer[field.predictedKey] as string | number | undefined) : undefined;
        const compareEnum = getCompareEnum(value, predictedValue);

        const elementKey = `${offer.advertisementId}-${field.valueKey}`;
        const elementState = infoElementsState[elementKey] || { hover: false, clicked: false };
        const showTooltip = elementState.hover || isTouchDevice() && elementState.clicked;

        let displayValue: string | number | undefined = value;

        if (compareEnum === ValueCompareEnum.DIFFERENT_VALUES && showTooltip) {
            displayValue = predictedValue;
        } else if (compareEnum === ValueCompareEnum.PREDICTED_VALUE_ONLY) {
            displayValue = predictedValue;
        } else if (compareEnum === ValueCompareEnum.ORIGINAL_VALUE_ONLY) {
            displayValue = value;
        }

        const variant = getVariantFromCompareEnum(compareEnum);
        const isBlurred = !hasPredictionRole && (compareEnum === ValueCompareEnum.DIFFERENT_VALUES && showTooltip || compareEnum === ValueCompareEnum.PREDICTED_VALUE_ONLY);

        const minWidth = calculateMinElementWidth(value, predictedValue, field.prefix, field.suffix, variant);
        const children = <>
            {field.prefix && field.prefix}
            <b>{field.valueKey === 'module' ? getNameFromModule(value as Module) : displayValue}</b>
            {field.suffix && field.suffix}
            {field.icon === IconEnum.SQUARE && <>m<sup>2</sup></>}
        </>;

        const getTooltipText = () => {
            if (compareEnum === ValueCompareEnum.DIFFERENT_VALUES || compareEnum === ValueCompareEnum.PREDICTED_VALUE_ONLY) {
                return field.tooltipText
                    ? <div><p>{field.tooltipText}</p><p>{tooltipText}</p>{!hasPredictionRole && <p>Opcja niedostępna w wybranym pakiecie</p>}</div>
                    : <div><p>{tooltipText}</p>{!hasPredictionRole && <p>Opcja niedostępna w wybranym pakiecie</p>}</div>;
            } else return field.tooltipText;
        };

        return {
            icon: field.icon,
            isVisible: !!value || !!predictedValue,
            children,
            variant,
            isBlurred,
            minWidth,
            elementKey,
            value: displayValue,
            prefix: field.prefix,
            suffix: field.suffix,
            tooltipText: getTooltipText(),
            compareEnum,
            showTooltip,
            onMouseEnter: () => setInfoElementHover?.(offer.advertisementId, field.valueKey as string, true),
            onMouseLeave: () => setInfoElementHover?.(offer.advertisementId, field.valueKey as string, false),
            onClick: () => setInfoElementClicked?.(offer.advertisementId, field.valueKey as string, !elementState.clicked)
        } as InfoElementType;
    });
};

const generateOfferDetailsTabContent = (
    offer: Offer,
    detailsInfoElementsState: InfoElementsStateType,
    hasPredictionRole: boolean,
    setDetailsInfoElementHover?: (offerId: number, elementKey: string, hover: boolean) => void,
    setDetailsInfoElementClicked?: (offerId: number, elementKey: string, clicked: boolean) => void
): V2DetailsTabsProps => {
    const price = offer.price || offer.priceBeforeModification;

    const fieldsConfig: FieldConfig[] = [
        {
            icon: IconEnum.SQUARE_TIMER,
            label: 'Ostatnia modyfikacja',
            value: (offer: Offer) => offer.lastUpdated && offer.lastUpdated !== offer.dateAdded
                ? dayjs(offer.lastUpdated).format('DD.MM.YYYY, HH:mm')
                : undefined
        },
        {
            icon: IconEnum.CALENDAR,
            label: 'Data dodania',
            value: (offer: Offer) => dayjs(offer.dateAdded).format('DD.MM.YYYY, HH:mm')
        },
        {
            icon: IconEnum.CALENDAR,
            label: 'Pierwsza publikacja',
            value: (offer: Offer) => offer.oldestDuplicateDateAdded
                ? dayjs(offer.oldestDuplicateDateAdded).format('DD.MM.YYYY, HH:mm')
                : undefined
        },
        {
            icon: IconEnum.PRICE,
            label: 'Aktualna cena',
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            value: (offer: Offer) => price ? `${price} złotych` : undefined
        },
        {
            icon: IconEnum.PRICE_PART,
            label: 'Aktualna cena za m²',
            value: (offer: Offer) => offer.pricePerSquareMeter ? `${offer.pricePerSquareMeter} zł/m²` : undefined
        },
        {
            icon: IconEnum.SQUARE,
            label: 'Metraż',
            value: (offer: Offer) => offer.area ? `${offer.area} m²` : undefined,
            predictedValue: (offer: Offer) => offer.predictedArea ? `${offer.predictedArea} m²` : undefined
        },
        {
            icon: IconEnum.BED,
            label: 'Liczba pokoi',
            value: (offer: Offer) => offer.numberOfRooms?.toString(),
            predictedValue: (offer: Offer) => offer.predictedNumberOfRooms?.toString()
        },
        {
            icon: IconEnum.FLOOR_LEVEL,
            label: 'Piętro',
            value: (offer: Offer) => `${offer.floor?.toString()} piętro`,
            predictedValue: (offer: Offer) => offer.predictedFloor?.toString()
        },

        {
            icon: IconEnum.LEAVE,
            label: 'Typ nieruchomości',
            value: (offer: Offer) => offer.module ? getNameFromModule(offer.module) : undefined
        },
        {
            icon: IconEnum.TWO_TYPE,
            label: 'Rodzaj budynku',
            value: (offer: Offer) => offer.buildingType,
            predictedValue: (offer: Offer) => offer.predictedBuildingType
        },
        {
            icon: IconEnum.CALENDAR,
            label: 'Rok budowy',
            value: (offer: Offer) => offer.builtYear?.toString(),
            predictedValue: (offer: Offer) => offer.predictedBuiltYear?.toString()
        },
        {
            icon: IconEnum.WORK,
            label: 'Rodzaj rynku',
            value: (offer: Offer) => offer.typeOfMarket,
            predictedValue: (offer: Offer) => offer.predictedTypeOfMarket
        },
        {
            icon: IconEnum.PEOPLE,
            label: 'Oferta od',
            value: (offer: Offer) => offer.offerFrom,
            predictedValue: (offer: Offer) => offer.predictedOfferFrom
        },
        {
            icon: IconEnum.LINK,
            label: 'Portal',
            value: (offer: Offer) => offer.portal,
            url: (offer: Offer) => offer.link
        },
        {
            icon: IconEnum.PAINT_ROLLER,
            label: 'Stan techniczny nieruchomości',
            value: (offer: Offer) => offer.buildingCondition,
            predictedValue: (offer: Offer) => offer.predictedBuildingCondition
        }
    ];

    const buildDetailElement = (field: typeof fieldsConfig[0], index: number) => {
        const value = field.value(offer);
        const predicted = field.predictedValue?.(offer);
        if (!value && !predicted) return null;

        const compareEnum = getCompareEnum(value, predicted);

        const elementKey = `${offer.advertisementId}-${field.label}-${index}`;
        const elementState = detailsInfoElementsState[elementKey] || { hover: false, clicked: false };
        const showTooltip = elementState.hover || isTouchDevice() && elementState.clicked;

        let displayValue: string | undefined = value;

        if (compareEnum === ValueCompareEnum.DIFFERENT_VALUES && showTooltip) {
            displayValue = predicted;
        } else if (compareEnum === ValueCompareEnum.PREDICTED_VALUE_ONLY) {
            displayValue = predicted;
        } else if (compareEnum === ValueCompareEnum.ORIGINAL_VALUE_ONLY) {
            displayValue = value;
        }

        const variant = getVariantFromCompareEnum(compareEnum);
        const isBlurred = !hasPredictionRole && (compareEnum === ValueCompareEnum.DIFFERENT_VALUES && showTooltip || compareEnum === ValueCompareEnum.PREDICTED_VALUE_ONLY);
        const minWidth = calculateMinElementWidth(value, predicted, field.prefix, field.suffix, variant);

        const tooltipTextDetailElement = compareEnum ? <div><p>{tooltipText}</p>{!hasPredictionRole && <p>Opcja niedostępna w wybranym pakiecie</p>}</div> : undefined;

        return {
            icon: field.icon,
            text: field.label,
            value: displayValue,
            variant,
            isBlurred,
            minWidth,
            elementKey,
            prefix: field.prefix,
            suffix: field.suffix,
            tooltipText: tooltipTextDetailElement,
            showTooltip,
            onMouseEnter: predicted
                ? () => setDetailsInfoElementHover?.(offer.advertisementId, `${field.label}-${index}`, true)
                : undefined,
            onMouseLeave: predicted
                ? () => setDetailsInfoElementHover?.(offer.advertisementId, `${field.label}-${index}`, false)
                : undefined,
            onClick: predicted
                ? () => setDetailsInfoElementClicked?.(offer.advertisementId, `${field.label}-${index}`, !elementState.clicked)
                : undefined,
            url: field.url ? field.url(offer) : undefined
        };
    };

    const detailsElements = fieldsConfig
        .map((field, index) => buildDetailElement(field, index))
        .filter(Boolean) as V2DetailsTabsProps['detailsElements'];

    return {
        title: offer.title,
        address: offer.location,
        predictedAddress: offer.predictedLocation,
        detailsElements,
        description: offer.detailedContent?.description,
        meta: offer.detailedContent && offer.detailedContent.metaValue ? normalizeMetaKeyWords(offer.detailedContent.metaValue) : [],
        predictedPrice: offer.predictedPrice ? offer.predictedPrice.toString() : undefined,
        calculationValue: offer.flipResult?.maxPurchasePrice ? offer.flipResult.maxPurchasePrice.toString() : undefined
    };
};

export interface MetaValueElementType {
    metaKey: string;
    value?: string;
    aiValue?: string;
    onMouseEnter?: () => void;
    onMouseLeave?: () => void;
    onClick?: () => void;
    showTooltip?: boolean;
}

const getDateIcon = (status?: SaleStatusDto): IconEnum | undefined => {
    if (!status) return undefined;

    const foundKey = Object.entries(status).find(([, value]) => value)![0] as keyof SaleStatusDto;

    switch (foundKey) {
    case 'isModification':
        return IconEnum.EDIT_WHITE;
    case 'isTossedUp':
        return IconEnum.ALERT_WHITE;
    case 'isNew':
    default:
        return undefined;
    }
};

export const saleOfferListFunctions = {
    generateInfoElements,
    generateOfferDetailsTabContent,
    getDateIcon
};
